import React, { useEffect } from 'react';
import type { InfoBannerCollectionElement } from '../../graphql/pimcore/modules/siteContent/types/SiteContentElement';
import InfoBanner from './InfoBanner';

interface InfoBannerListProps {
    banners: InfoBannerCollectionElement;
}

/**
 * Cleans up old info banner keys in localStorage that are no longer available.
 * @param prefix The prefix used to check if the key belongs to info banners and should be considered for cleanup.
 * @param activeIds The currently active info banner identifiers (they already contain the prefix).
 */
function cleanOldInfoBannerKeys(prefix: string, activeIds: string[]) {
    if (!prefix || prefix.trim() === '') {
        // Prevent cleaning if no prefix is set (error case)
        return;
    }

    const activeKeys = new Set([...activeIds]);

    for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key?.startsWith(prefix) && !activeKeys.has(key)) {
            localStorage.removeItem(key);
        }
    }
}

const InfoBannerList: React.FC<InfoBannerListProps> = ({ banners }) => {
    // Clean up old info banner keys in localStorage
    useEffect(() => {
        const activeIds = banners.entries.map((entry) => entry.identifier);
        cleanOldInfoBannerKeys(banners.entriesPrefix, activeIds);
    }, [banners]);

    return (
        <>
            {banners.entries.map((entry) => (
                <InfoBanner
                    key={entry.identifier}
                    identifier={entry.identifier}
                    content={entry.sanitizedContent}
                />
            ))}
        </>
    );
};

export default InfoBannerList;
